/**
 * API Client for SourceMyGoods
 * Handles all API requests to the backend
 */

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sourcemygoods.com/api/controllers';

// Helper to get auth token from localStorage
const getAuthToken = (): string | null => {
  const user = localStorage.getItem('shiphub-user');
  if (!user) return null;

  const userData = JSON.parse(user);
  return userData.token || null;
};

// Helper to make authenticated requests
const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
};

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await fetch(`${API_BASE_URL}/auth.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    return response.json();
  },

  register: async (data: {
    username: string;
    password: string;
    customer_name: string;
    email: string;
    phone: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    destination_warehouse?: string;
  }) => {
    const response = await fetch(`${API_BASE_URL}/auth.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Registration failed');
    }

    return response.json();
  },
};

// Customers API
export const customersAPI = {
  getAll: () => fetchWithAuth(`${API_BASE_URL}/customers.php`),

  getOne: (code: string) => fetchWithAuth(`${API_BASE_URL}/customers.php?code=${code}`),

  create: (data: any) => fetchWithAuth(`${API_BASE_URL}/customers.php`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (data: any) => fetchWithAuth(`${API_BASE_URL}/customers.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (code: string) => fetchWithAuth(`${API_BASE_URL}/customers.php?code=${code}`, {
    method: 'DELETE',
  }),
};

// Warehouses API
export const warehousesAPI = {
  getAll: () => fetchWithAuth(`${API_BASE_URL}/warehouses.php`),

  // Public endpoint for registration form (no auth required)
  getPublic: async () => {
    const response = await fetch(`${API_BASE_URL.replace('/controllers', '/public')}/warehouses.php`);
    if (!response.ok) {
      throw new Error('Failed to fetch warehouses');
    }
    return response.json();
  },

  getOne: (code: string) => fetchWithAuth(`${API_BASE_URL}/warehouses.php?code=${code}`),

  create: (data: any) => fetchWithAuth(`${API_BASE_URL}/warehouses.php`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (data: any) => fetchWithAuth(`${API_BASE_URL}/warehouses.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (code: string) => fetchWithAuth(`${API_BASE_URL}/warehouses.php?code=${code}`, {
    method: 'DELETE',
  }),
};

// Shipments API
export const shipmentsAPI = {
  getAll: () => fetchWithAuth(`${API_BASE_URL}/shipments.php`),

  getOne: (shipmentNumber: string) =>
    fetchWithAuth(`${API_BASE_URL}/shipments.php?shipment_number=${shipmentNumber}`),

  create: (data: any) => fetchWithAuth(`${API_BASE_URL}/shipments.php`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (data: any) => fetchWithAuth(`${API_BASE_URL}/shipments.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (shipmentNumber: string) =>
    fetchWithAuth(`${API_BASE_URL}/shipments.php?shipment_number=${shipmentNumber}`, {
    method: 'DELETE',
  }),
};

// Trips API
export const tripsAPI = {
  getAll: () => fetchWithAuth(`${API_BASE_URL}/trips.php`),

  getOne: (tripNumber: string) =>
    fetchWithAuth(`${API_BASE_URL}/trips.php?trip_number=${tripNumber}`),

  create: (data: any) => fetchWithAuth(`${API_BASE_URL}/trips.php`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (data: any) => fetchWithAuth(`${API_BASE_URL}/trips.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (tripNumber: string) =>
    fetchWithAuth(`${API_BASE_URL}/trips.php?trip_number=${tripNumber}`, {
    method: 'DELETE',
  }),
};

// Invoices API
export const invoicesAPI = {
  getAll: () => fetchWithAuth(`${API_BASE_URL}/invoices.php`),

  getOne: (invoiceNumber: string) =>
    fetchWithAuth(`${API_BASE_URL}/invoices.php?invoice_number=${invoiceNumber}`),

  create: (data: any) => fetchWithAuth(`${API_BASE_URL}/invoices.php`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (data: any) => fetchWithAuth(`${API_BASE_URL}/invoices.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (invoiceNumber: string) =>
    fetchWithAuth(`${API_BASE_URL}/invoices.php?invoice_number=${invoiceNumber}`, {
    method: 'DELETE',
  }),
};

// Users API
export const usersAPI = {
  getAll: () => fetchWithAuth(`${API_BASE_URL}/users.php`),

  getOne: (id: number) => fetchWithAuth(`${API_BASE_URL}/users.php?id=${id}`),

  create: (data: any) => fetchWithAuth(`${API_BASE_URL}/users.php`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (data: any) => fetchWithAuth(`${API_BASE_URL}/users.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id: number) => fetchWithAuth(`${API_BASE_URL}/users.php?id=${id}`, {
    method: 'DELETE',
  }),
};

// Public Tracking API (no auth required)
export const trackingAPI = {
  track: async (shipmentNumber: string) => {
    const response = await fetch(`${API_BASE_URL}/track.php?shipment_number=${shipmentNumber}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  },
};

// Shipment Items API
export const shipmentItemsAPI = {
  getAll: (shipmentNumber: string) => 
    fetchWithAuth(`${API_BASE_URL}/shipment-items.php?shipment_number=${shipmentNumber}`),

  create: (data: any) => fetchWithAuth(`${API_BASE_URL}/shipment-items.php`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (data: any) => fetchWithAuth(`${API_BASE_URL}/shipment-items.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (id: string) => fetchWithAuth(`${API_BASE_URL}/shipment-items.php?id=${id}`, {
    method: 'DELETE',
  }),
};

// Sourcing Requests API
export const sourcingRequestsAPI = {
  getAll: () => fetchWithAuth(`${API_BASE_URL}/sourcing-requests.php`),

  getOne: (requestNumber: string) => 
    fetchWithAuth(`${API_BASE_URL}/sourcing-requests.php?request_number=${requestNumber}`),

  create: (data: any) => fetchWithAuth(`${API_BASE_URL}/sourcing-requests.php`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  update: (data: any) => fetchWithAuth(`${API_BASE_URL}/sourcing-requests.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  delete: (requestNumber: string) => 
    fetchWithAuth(`${API_BASE_URL}/sourcing-requests.php?request_number=${requestNumber}`, {
    method: 'DELETE',
  }),
};

// Wallet API
export const walletAPI = {
  // Get wallet by customer code
  getWallet: (customerCode: string) =>
    fetchWithAuth(`${API_BASE_URL}/wallet.php?customer_code=${customerCode}`),

  // Get all wallets (admin only)
  getAllWallets: () => fetchWithAuth(`${API_BASE_URL}/wallet.php`),

  // Get wallet transactions
  getTransactions: (customerCode: string, limit?: number) =>
    fetchWithAuth(`${API_BASE_URL}/wallet.php?customer_code=${customerCode}&transactions=true${limit ? `&limit=${limit}` : ''}`),

  // Add credit to wallet (admin only)
  addCredit: (data: {
    customerCode: string;
    currency: string;
    amount: number;
    description: string;
  }) => fetchWithAuth(`${API_BASE_URL}/wallet.php`, {
    method: 'POST',
    body: JSON.stringify({ action: 'add_credit', ...data }),
  }),

  // Transfer money to another customer
  transfer: (data: {
    fromCustomerCode: string;
    toCustomerCode: string;
    currency: string;
    amount: number;
    description?: string;
  }) => fetchWithAuth(`${API_BASE_URL}/wallet.php`, {
    method: 'POST',
    body: JSON.stringify({ action: 'transfer', ...data }),
  }),

  // Exchange currency
  exchange: (data: {
    customerCode: string;
    fromCurrency: string;
    toCurrency: string;
    amount: number;
  }) => fetchWithAuth(`${API_BASE_URL}/wallet.php`, {
    method: 'POST',
    body: JSON.stringify({ action: 'exchange', ...data }),
  }),

  // Pay invoice with wallet
  payInvoice: (data: {
    customerCode: string;
    invoiceNumber: string;
    currency: string;
    amount: number;
  }) => fetchWithAuth(`${API_BASE_URL}/wallet.php`, {
    method: 'POST',
    body: JSON.stringify({ action: 'pay_invoice', ...data }),
  }),

  // Pay sourcing request with wallet
  paySourcingRequest: (data: {
    customerCode: string;
    requestNumber: string;
    currency: string;
    amount: number;
  }) => fetchWithAuth(`${API_BASE_URL}/wallet.php`, {
    method: 'POST',
    body: JSON.stringify({ action: 'pay_sourcing', ...data }),
  }),

  // Update wallet status (admin only)
  updateStatus: (customerCode: string, status: string) =>
    fetchWithAuth(`${API_BASE_URL}/wallet.php`, {
      method: 'PUT',
      body: JSON.stringify({ customerCode, status }),
    }),
};

// Exchange Rate API
export const exchangeRateAPI = {
  // Get current exchange rates
  getRates: () => fetchWithAuth(`${API_BASE_URL}/exchange-rates.php`),

  // Update exchange rate (admin only)
  updateRate: (data: {
    fromCurrency: string;
    toCurrency: string;
    rate: number;
  }) => fetchWithAuth(`${API_BASE_URL}/exchange-rates.php`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};
